import React, { useEffect, useRef, useState, useCallback } from 'react';
import Gantt from 'frappe-gantt';
import { Select, message, Skeleton, Radio, Badge, Tooltip } from 'antd';
import Text from 'antd/lib/typography/Text';
import { useProjectContext } from '../../../../core/components/projects/useProjectContext';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import moment from 'moment';
import '../styles/ProjectGantt.css';

const { Option } = Select;

const ProjectGantt = ({ currentProjectId }) => {
  const ganttRef = useRef(null);
  const ganttInstance = useRef(null);
  const [viewMode, setViewMode] = useState('Week');
  const dateChangeTimeoutRef = useRef(null);
  const progressChangeTimeoutRef = useRef(null);

  const {
    currentProject,
    fetchMilestones,
    milestones,
    milestonesLoading,
    refetchProject,
    updateMilestone,
  } = useProjectContext();

  const { updateTask } = useTaskContext();

  // Refetch on mount or when project changes
  useEffect(() => {
    if (currentProjectId) {
      // Fetch project tasks
      if (refetchProject) {
        refetchProject();
      }

      // Fetch milestones
      fetchMilestones({
        variables: {
          where: { project: { id: currentProjectId } },
          orderBy: 'dueDate_ASC',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectId]);

  // Refetch when user returns to the tab/page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentProjectId) {
        console.log('Tab became visible - refetching data');
        if (refetchProject) {
          refetchProject();
        }
        fetchMilestones({
          variables: {
            where: { project: { id: currentProjectId } },
            orderBy: 'dueDate_ASC',
          },
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectId]);

  const [taskLabels, setTaskLabels] = useState([]);
  const sidebarRef = useRef(null);

  // Extract stable dependencies for useEffect
  const projectId = currentProject?.id;
  const milestonesCount = milestones?.length;

  const prepareGanttData = useCallback(() => {
    if (!currentProject || !milestones) {
      return { tasks: [], labels: [] };
    }

    const tasks = [];
    const labels = []; 

    // Get all tasks assigned to milestones
    const tasksInMilestones = new Set();
    milestones.forEach(milestone => {
      milestone.tasks?.forEach(task => tasksInMilestones.add(task.id));
    });

    // Add all milestone groups with their tasks
    milestones.forEach((milestone, index) => {
      const milestoneTasks = milestone.tasks || [];

      if (milestoneTasks.length === 0) return;

      // Use milestone's own dates, not task dates
      // If milestone has startDate, use it; otherwise use earliest task start
      let milestoneStart = milestone.startDate ? moment(milestone.startDate) : null;
      let milestoneEnd = milestone.dueDate ? moment(milestone.dueDate) : null;

      // Fallback to task dates if milestone dates are missing
      if (!milestoneStart || !milestoneEnd) {
        const taskDates = milestoneTasks
          .map(t => ({
            start: t.startDate ? moment(t.startDate) : (t.createdAt ? moment(t.createdAt) : null),
            end: t.dueDate ? moment(t.dueDate) : null,
          }))
          .filter(d => d.start && d.end);

        if (taskDates.length === 0) return;

        if (!milestoneStart) {
          milestoneStart = moment.min(taskDates.map(d => d.start));
        }
        if (!milestoneEnd) {
          milestoneEnd = moment.max(taskDates.map(d => d.end));
        }
      }

      // Calculate completion percentage for milestone
      const completedCount = milestoneTasks.filter(t =>
        t.status === 'COMPLETED' || t.status === 'DONE'
      ).length;
      const completionPercent = milestoneTasks.length > 0
        ? Math.round((completedCount / milestoneTasks.length) * 100)
        : 0;

      // Add milestone header bar using milestone's own dates
      tasks.push({
        id: `milestone-header-${milestone.id}`,
        name: `━━━ ${milestone.name} (${completionPercent}% Complete) ━━━`,
        start: milestoneStart.format('YYYY-MM-DD'),
        end: milestoneEnd.format('YYYY-MM-DD'),
        progress: completionPercent,
        custom_class: `milestone-header-color-${(index % 5) + 1}`,
        is_milestone_header: true,
        milestone_id: milestone.id,
        milestone_index: index,
      });

      // Add milestone header label
      labels.push({
        id: `milestone-header-${milestone.id}`,
        text: `📍 ${milestone.name}`,
        type: 'milestone-header',
        completionPercent: completionPercent
      });

      // Add milestone tasks
      milestoneTasks.forEach((task, taskIndex) => {
        const startDate = task.startDate ? moment(task.startDate) : (task.createdAt ? moment(task.createdAt) : moment());
        const endDate = task.dueDate ? moment(task.dueDate) : moment().add(7, 'days');

        const completedStatuses = ['COMPLETED', 'DONE'];
        const progress = completedStatuses.includes(task.status) ? 100 : 0;

        // Get assignee names
        const assigneeNames = task.assigneeUsers?.map(u => u.name || u.username).join(', ') || '';

        tasks.push({
          id: task.id,
          name: `  ${taskIndex + 1}. ${task.name}`,
          task_name: task.name, // Original task name for popup
          task_status: task.status,
          assignees: assigneeNames,
          start: startDate.format('YYYY-MM-DD'),
          end: endDate.format('YYYY-MM-DD'),
          progress: progress,
          custom_class: `milestone-task-color-${(index % 5) + 1}`,
          milestone_name: milestone.name,
          milestone_id: milestone.id,
          milestone_index: index,
        });

        // Add task label
        labels.push({
          id: task.id,
          text: task.name,
          type: 'task',
          status: task.status
        });
      });

      // Add section separator (invisible spacer) after each milestone except the last
      if (index < milestones.length - 1) {
        tasks.push({
          id: `separator-${milestone.id}`,
          name: '',
          start: moment().format('YYYY-MM-DD'),
          end: moment().format('YYYY-MM-DD'),
          progress: 0,
          custom_class: `milestone-separator-${index}`,
          is_separator: true,
        });

        // Add separator label
        labels.push({
          id: `separator-${milestone.id}`,
          text: '',
          type: 'separator'
        });
      }
    });


    return { tasks, labels };
  }, [currentProject, milestones]);

  useEffect(() => {
    const ganttElement = ganttRef.current;
    if (!ganttElement) return;

    const { tasks, labels } = prepareGanttData();

    setTaskLabels(labels);

    if (tasks.length === 0) {
      if (ganttInstance.current) {
        ganttInstance.current = null;
      }
      ganttElement.innerHTML = '<div style="padding: 40px; text-align: center; color: #94a3b8; font-size: 14px;">No tasks with valid dates found. Tasks need both a start date and due date to appear in the Gantt chart.</div>';
      return;
    }

    // Fixed date range: 2024-2027 only
    const dateRange = {
      start: moment('2024-01-01'),
      end: moment('2027-12-31')
    };

    // Adjust task dates to fit within the fixed range
    const adjustedTasks = tasks.map(task => {
      const taskStart = moment(task.start);
      const taskEnd = moment(task.end);

      return {
        ...task,
        start: taskStart.isBefore(dateRange.start) ? dateRange.start.format('YYYY-MM-DD') : task.start,
        end: taskEnd.isAfter(dateRange.end) ? dateRange.end.format('YYYY-MM-DD') : task.end,
      };
    });

    // Clear previous instance
    if (ganttInstance.current) {
      ganttInstance.current = null;
    }

    // Clear the container
    ganttElement.innerHTML = '';

    // Create new instance
    try {
      ganttInstance.current = new Gantt(ganttElement, adjustedTasks, {
        view_mode: viewMode,
        view_start: dateRange.start.format('YYYY-MM-DD'),
        view_end: dateRange.end.format('YYYY-MM-DD'),
        bar_height: 40,
        bar_corner_radius: 4,
        arrow_curve: 5,
        padding: 24,
        container_height: 'auto',
        date_format: 'YYYY-MM-DD',
        language: 'en',
        popup_trigger: 'click',
        custom_popup_html: (task) => {
          if (task.is_milestone_header || task.is_separator) {
            return null;
          }

          const statusColors = {
            'COMPLETED': '#10b981',
            'DONE': '#10b981',
            'IN_PROGRESS': '#3b82f6',
            'PENDING': '#94a3b8',
            'BLOCKED': '#ef4444'
          };

          const statusColor = statusColors[task.task_status] || '#94a3b8';

          return `
            <div class="gantt-popup-container">
              <div class="gantt-popup-header">
                <a href="/tasks/details/${task.id}" class="gantt-popup-title" data-task-id="${task.id}">
                  ${task.task_name || task.name}
                </a>
              </div>
              <div class="gantt-popup-body">
                <div class="gantt-popup-row">
                  <span class="gantt-popup-label">Status:</span>
                  <span class="gantt-popup-badge" style="background-color: ${statusColor};">
                    ${task.task_status || 'PENDING'}
                  </span>
                </div>
                ${task.assignees ? `
                  <div class="gantt-popup-row">
                    <span class="gantt-popup-label">Assigned to:</span>
                    <span class="gantt-popup-value">${task.assignees}</span>
                  </div>
                ` : ''}
                ${task.milestone_name ? `
                  <div class="gantt-popup-row">
                    <span class="gantt-popup-label">Milestone:</span>
                    <span class="gantt-popup-value">${task.milestone_name}</span>
                  </div>
                ` : ''}
                <div class="gantt-popup-row">
                  <span class="gantt-popup-label">Start:</span>
                  <span class="gantt-popup-value">${moment(task.start).format('MMM D, YYYY')}</span>
                </div>
                <div class="gantt-popup-row">
                  <span class="gantt-popup-label">Due:</span>
                  <span class="gantt-popup-value">${moment(task.end).format('MMM D, YYYY')}</span>
                </div>
                <div class="gantt-popup-row">
                  <span class="gantt-popup-label">Progress:</span>
                  <span class="gantt-popup-value">${task.progress}%</span>
                </div>
              </div>
              <div class="gantt-popup-footer">
                <span class="gantt-popup-hint">Click task name in sidebar to view details • Drag bar to reschedule</span>
              </div>
            </div>
          `;
        },
        on_date_change: (task, start, end) => {
          // Handle milestone header drag
          if (task.is_milestone_header) {
            const oldStart = moment(task._start);
            const oldEnd = moment(task._end);
            const newStart = moment(start);
            const newEnd = moment(end);

            const originalDuration = oldEnd.diff(oldStart, 'days');
            const startDiff = newStart.diff(oldStart, 'days');
            const endDiff = newEnd.diff(oldEnd, 'days');
            const isBarDrag = Math.abs(startDiff - endDiff) <= 1 && startDiff !== 0;

            console.log('Milestone date change:', {
              milestoneId: task.milestone_id,
              oldStart: oldStart.format('YYYY-MM-DD'),
              newStart: newStart.format('YYYY-MM-DD'),
              oldEnd: oldEnd.format('YYYY-MM-DD'),
              newEnd: newEnd.format('YYYY-MM-DD'),
              isBarDrag,
            });

            // Clear any existing timeout
            if (dateChangeTimeoutRef.current) {
              clearTimeout(dateChangeTimeoutRef.current);
            }

            // Update milestone dates
            dateChangeTimeoutRef.current = setTimeout(async () => {
              try {
                let finalStart = newStart.clone();
                let finalEnd = newEnd.clone();

                if (isBarDrag) {
                  finalStart = newStart.clone();
                  finalEnd = newStart.clone().add(originalDuration, 'days');
                }

                const updateData = {
                  startDate: finalStart.toISOString(),
                  dueDate: finalEnd.toISOString(),
                };

                await updateMilestone({
                  variables: {
                    data: updateData,
                    where: { id: task.milestone_id },
                  },
                });

                // Update task object
                task._start = finalStart.toDate();
                task._end = finalEnd.toDate();
                task.start = finalStart.format('YYYY-MM-DD');
                task.end = finalEnd.format('YYYY-MM-DD');

                message.success('Milestone dates updated', 1);
              } catch (error) {
                console.error('Failed to update milestone dates:', error);
                message.error('Failed to update milestone dates');
              }
            }, 500);
            return;
          }

          // Ignore separator
          if (task.is_separator) return;

          const oldStart = moment(task._start);
          const oldEnd = moment(task._end);
          const newStart = moment(start);
          const newEnd = moment(end);

          // Calculate the original duration in days
          const originalDuration = oldEnd.diff(oldStart, 'days');

          // Check if this is a whole-bar drag (both dates changed by the same amount)
          // or a resize operation (only one end changed)
          const startDiff = newStart.diff(oldStart, 'days');
          const endDiff = newEnd.diff(oldEnd, 'days');
          const isBarDrag = Math.abs(startDiff - endDiff) <= 1 && startDiff !== 0;

          console.log('Gantt date change:', {
            taskId: task.id,
            taskName: task.name,
            oldStart: oldStart.format('YYYY-MM-DD'),
            newStart: newStart.format('YYYY-MM-DD'),
            oldEnd: oldEnd.format('YYYY-MM-DD'),
            newEnd: newEnd.format('YYYY-MM-DD'),
            startDiff,
            endDiff,
            isBarDrag,
            originalDuration,
          });

          // Clear any existing timeout
          if (dateChangeTimeoutRef.current) {
            clearTimeout(dateChangeTimeoutRef.current);
          }

          // Set a new timeout to update after user stops dragging
          dateChangeTimeoutRef.current = setTimeout(async () => {
            try {
              let finalStart = newStart.clone();
              let finalEnd = newEnd.clone();

              // If this is a bar drag (moving entire task), maintain the original duration
              if (isBarDrag) {
                finalStart = newStart.clone();
                finalEnd = newStart.clone().add(originalDuration, 'days');
                console.log('Bar drag detected - maintaining duration:', {
                  originalDuration,
                  finalStart: finalStart.format('YYYY-MM-DD'),
                  finalEnd: finalEnd.format('YYYY-MM-DD')
                });
              } else {
                // This is a resize operation - use the new dates as-is
                console.log('Resize detected - using new dates');
              }

              const updateData = {
                startDate: finalStart.toISOString(),
                dueDate: finalEnd.toISOString(),
              };

              console.log('Updating task with:', updateData);

              // Update the task in the background without refetching
              // This keeps the UI smooth and prevents reload/flash
              await updateTask({
                variables: {
                  data: updateData,
                  where: { id: task.id },
                },
              });

              // Update the task object's internal dates so future drags work correctly
              task._start = finalStart.toDate();
              task._end = finalEnd.toDate();
              task.start = finalStart.format('YYYY-MM-DD');
              task.end = finalEnd.format('YYYY-MM-DD');

              // Show subtle success message
              message.success('Task dates updated', 1);

              // NO REFETCH - UI already updated optimistically by the drag
              // The chart stays smooth without reloading
            } catch (error) {
              console.error('Failed to update task dates:', error);
              message.error('Failed to update task dates');
            }
          }, 500); // Wait 500ms after user stops dragging
        },
        on_progress_change: (task, progress) => {
          if (task.is_milestone_header || task.is_separator) return;

          // Clear any existing timeout
          if (progressChangeTimeoutRef.current) {
            clearTimeout(progressChangeTimeoutRef.current);
          }

          // Set a new timeout to update after user stops dragging
          progressChangeTimeoutRef.current = setTimeout(async () => {
            try {
              const newStatus = progress === 100 ? 'COMPLETED' : 'IN_PROGRESS';

              // Update in background without refetching
              await updateTask({
                variables: {
                  data: {
                    status: newStatus,
                  },
                  where: { id: task.id },
                },
              });

              // Update task object to reflect new progress
              task.progress = progress;

              // Show subtle success message
              message.success('Task status updated', 1);

              // NO REFETCH - UI already updated optimistically by the progress drag
              // The chart stays smooth without reloading
            } catch (error) {
              console.error('Failed to update task status:', error);
              message.error('Failed to update task status');
            }
          }, 500); // Wait 500ms after user stops dragging
        },
      });

      // Fix: Manually increase SVG height to prevent bottom cutoff
      const ganttContainer = ganttElement.querySelector('.gantt-container');
      const ganttSvg = ganttContainer?.querySelector('svg');
      if (ganttSvg) {
        const currentHeight = parseInt(ganttSvg.getAttribute('height') || '0');
        ganttSvg.setAttribute('height', (currentHeight + 50).toString());
      }

      // Sync scrolling between sidebar and Gantt chart (vertical only)
      if (ganttContainer && sidebarRef.current) {
        let ticking = false;

        const syncScroll = () => {
          const scrollTop = ganttContainer.scrollTop;

          if (!ticking) {
            window.requestAnimationFrame(() => {
              // Only sync vertical scrolling, not horizontal
              if (sidebarRef.current) {
                sidebarRef.current.scrollTop = scrollTop;
              }
              ticking = false;
            });
            ticking = true;
          }
        };

        // Allow vertical mousewheel scroll, but prevent horizontal (only horizontal scrollbar slider works)
        const preventHorizontalWheel = (e) => {
          // If user is scrolling vertically (shift not pressed), allow it
          if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && !e.shiftKey) {
            // Allow vertical scroll - do nothing
            return;
          }
          // Prevent horizontal scroll (shift+wheel or trackpad horizontal swipe)
          e.preventDefault();
          e.stopPropagation();
        };

        // Use passive listener to improve scroll performance
        ganttContainer.addEventListener('scroll', syncScroll, { passive: true });

        // Prevent horizontal mousewheel scroll (non-passive to allow preventDefault)
        ganttContainer.addEventListener('wheel', preventHorizontalWheel, { passive: false });

        return () => {
          ganttContainer.removeEventListener('scroll', syncScroll);
          ganttContainer.removeEventListener('wheel', preventHorizontalWheel);
        };
      }
    } catch (error) {
      console.error('Failed to initialize Gantt chart:', error);
    }

    return () => {
      // Clear any pending timeouts
      if (dateChangeTimeoutRef.current) {
        clearTimeout(dateChangeTimeoutRef.current);
      }
      if (progressChangeTimeoutRef.current) {
        clearTimeout(progressChangeTimeoutRef.current);
      }

      if (ganttInstance.current) {
        ganttInstance.current = null;
      }
      if (ganttElement) {
        ganttElement.innerHTML = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepareGanttData, viewMode, projectId, milestonesCount]);
  // Note: Excluding updateTask, refetchProject, fetchMilestones, and currentProjectId from dependencies
  // because they're used inside closures within the Gantt event handlers. Including them would cause
  // unnecessary chart recreation on every render.


  const getProjectTasksCount = () => {
    if (!currentProject || !milestones) return 0;

    const tasksInMilestones = new Set();
    milestones.forEach(milestone => {
      milestone.tasks?.forEach(task => tasksInMilestones.add(task.id));
    });

    return currentProject.tasks?.filter(task => !tasksInMilestones.has(task.id)).length || 0;
  };

  if (milestonesLoading) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <Text strong style={{ fontSize: '20px', color: '#1f2937' }}>Project Gantt Chart</Text>
        </div>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  const hasTasks = (currentProject?.tasks?.length || 0) > 0;

  if (!hasTasks) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <Text strong style={{ fontSize: '20px', color: '#1f2937' }}>Project Gantt Chart</Text>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <Text style={{ color: '#94a3b8', fontSize: '16px' }}>
            No tasks available to display in Gantt chart
          </Text>
        </div>
      </div>
    );
  }

  const unassignedTasksCount = getProjectTasksCount();

  return (
    <div>
      {/* Warning for tasks not in milestones */}
      {unassignedTasksCount > 0 && (
        <div style={{
          marginBottom: 16,
          padding: '8px 12px',
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <Text style={{ fontSize: '13px', color: '#64748b' }}>
            {unassignedTasksCount} task{unassignedTasksCount > 1 ? 's are' : ' is'} not assigned to any milestone.
          </Text>
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        paddingBottom: 16,
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Text strong style={{ fontSize: '20px', color: '#1f2937' }}>Timeline</Text>
        </div>

        {/* Asana-style Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Zoom Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Text style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Zoom:</Text>
            <Tooltip
              title="💡 For accurate date adjustments when dragging tasks, use Days or Weeks view for consistent results"
              placement="bottom"
            >
              <Radio.Group
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                buttonStyle="solid"
              >
              <Radio.Button
                value="Day"
                style={{
                  fontSize: '14px',
                  height: '36px',
                  padding: '0 16px',
                  lineHeight: '34px',
                  fontWeight: 500
                }}
              >
                Days
              </Radio.Button>
              <Radio.Button
                value="Week"
                style={{
                  fontSize: '14px',
                  height: '36px',
                  padding: '0 16px',
                  lineHeight: '34px',
                  fontWeight: 500
                }}
              >
                Weeks
              </Radio.Button>
              <Radio.Button
                value="Month"
                style={{
                  fontSize: '14px',
                  height: '36px',
                  padding: '0 16px',
                  lineHeight: '34px',
                  fontWeight: 500
                }}
              >
                Months
              </Radio.Button>
              <Radio.Button
                value="Year"
                style={{
                  fontSize: '14px',
                  height: '36px',
                  padding: '0 16px',
                  lineHeight: '34px',
                  fontWeight: 500
                }}
              >
                Years
              </Radio.Button>
            </Radio.Group>
            </Tooltip>
          </div>
        </div>
      </div>


      {/* Gantt Chart with Sidebar */}
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px',
          display: 'flex',
          gap: '0',
          position: 'relative',
          height: '700px',
          overflow: 'hidden'
        }}
      >
        {/* Task Labels Sidebar */}
        <div
          ref={sidebarRef}
          style={{
            width: '280px',
            flexShrink: 0,
            borderRight: '2px solid #e5e7eb',
            paddingRight: '16px',
            paddingTop: '85px', // Align with Gantt chart content (after headers)
            paddingBottom: '50px', // Match Gantt SVG extra height for scroll sync
            overflow: 'auto',
            height: '100%',
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
          }}
          className="gantt-sidebar"
        >
          {taskLabels.map((label) => {
            if (label.type === 'milestone-header') {
              return (
                <div
                  key={label.id}
                  style={{
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: '700',
                    fontSize: '14px',
                    color: '#1e40af',
                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                    padding: '0 12px',
                    borderRadius: '4px',
                    marginBottom: '0px'
                  }}
                >
                  {label.text}
                  <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#64748b' }}>
                    {label.completionPercent}%
                  </span>
                </div>
              );
            } else if (label.type === 'project-header') {
              return (
                <div
                  key={label.id}
                  style={{
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: '700',
                    fontSize: '14px',
                    color: '#075985',
                    backgroundColor: 'rgba(14, 165, 233, 0.08)',
                    padding: '0 12px',
                    borderRadius: '4px',
                    marginBottom: '0px'
                  }}
                >
                  {label.text}
                </div>
              );
            } else if (label.type === 'task' || label.type === 'project-task') {
              const isCompleted = label.status === 'COMPLETED' || label.status === 'DONE';
              return (
                <a
                  key={label.id}
                  href={`/tasks/details/${label.id}`}
                  style={{
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '13px',
                    color: isCompleted ? '#94a3b8' : '#374151',
                    padding: '0 12px 0 24px',
                    textDecoration: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  title={label.text}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: isCompleted ? '#10b981' : '#3b82f6',
                    marginRight: '8px',
                    flexShrink: 0
                  }} />
                  <span style={{
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {label.text}
                  </span>
                </a>
              );
            } else if (label.type === 'separator') {
              return (
                <div
                  key={label.id}
                  style={{
                    height: '64px'
                  }}
                />
              );
            }
            return null;
          })}
        </div>

        {/* Gantt Timeline */}
        <div
          ref={ganttRef}
          style={{
            flex: 1,
            position: 'relative',
            minWidth: 0,
            height: '100%',
            overflow: 'hidden'
          }}
        />
      </div>

    </div>
  );
};

export default ProjectGantt;
